import { useContext, useEffect } from "react";
import { AuthContext } from "context";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import MDProgress from "components/MDProgress";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import { useUpgrade, PLANS, PREMIUM_BENEFITS } from "context/upgrade";

const getPlanLabel = (user) => {
  if (!user?.is_premium) return "Free";
  if (user.subscription_status === "lifetime") return "Premium Vitalício";
  if (user.subscription_plan === "yearly") return "Premium Anual";
  if (user.subscription_plan === "monthly") return "Premium Mensal";
  return "Premium";
};

function Billing() {
  const { user, getUserData } = useContext(AuthContext);
  const { startCheckout, openPortal, pendingAction } = useUpgrade();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  // Cota muda a cada liberação: busca o /me atualizado ao abrir a tela
  useEffect(() => {
    if (getUserData) getUserData();
  }, []);

  const isPremium = Boolean(user?.is_premium);
  // Vitalício: unlocks_unlimited = true e cota/restantes vêm null
  const unlimited = Boolean(user?.unlocks_unlimited);
  const quota = user?.unlocks_quota || 0;
  const used = user?.unlocks_used_this_month || 0;
  const remaining = user?.unlocks_remaining || 0;
  const busy = Boolean(pendingAction);
  // Pix na liberação avulsa depende de STRIPE_PIX_ENABLED na API
  const payMethods = user?.pix_enabled ? "cartão ou Pix" : "cartão";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h5" fontWeight="medium">
                    Meu Plano
                  </MDTypography>
                  <Icon fontSize="large" color="inherit">workspace_premium</Icon>
                </MDBox>

                <MDBox mb={3} display="flex" flexDirection="column" alignItems="center" py={2} bgColor="grey-100" borderRadius="lg">
                  <MDTypography variant="button" color="text" fontWeight="regular" mb={1}>
                    Plano atual
                  </MDTypography>
                  <MDBadge
                    badgeContent={user ? getPlanLabel(user) : "..."}
                    color={isPremium ? "success" : "secondary"}
                    variant="gradient"
                    size="lg"
                  />
                </MDBox>

                {/* Cota de liberações */}
                <MDBox mb={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={1}>
                    Liberações de pedido
                  </MDTypography>
                  {unlimited ? (
                    <MDTypography variant="button" color="text" display="block">
                      <strong>Ilimitadas</strong> no plano vitalício, inclusive em pedidos com as vagas
                      de DJ esgotadas. {used} {used === 1 ? "liberação" : "liberações"} este mês.
                    </MDTypography>
                  ) : isPremium ? (
                    <>
                      <MDTypography variant="button" color="text" display="block" mb={1}>
                        {used} de {quota} usadas este mês. Restam <strong>{remaining}</strong>.
                      </MDTypography>
                      <MDProgress
                        variant="gradient"
                        color={remaining > 0 ? "success" : "warning"}
                        value={quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0}
                      />
                      <MDTypography variant="caption" color="text" display="block" mt={1}>
                        A cota reinicia no dia 1 de cada mês. Sem cota, você ainda pode liberar pedidos avulsos.
                      </MDTypography>
                    </>
                  ) : (
                    <MDTypography variant="button" color="text" display="block">
                      O plano Free não inclui liberações. Você pode liberar qualquer pedido pagando
                      avulso ({payMethods}), sem precisar assinar.
                    </MDTypography>
                  )}
                </MDBox>

                {user?.subscription_status === "lifetime" ? (
                  // Vitalício é concedido manualmente: sem conta no Stripe, nada para gerenciar
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon color="success">verified</Icon>
                    <MDTypography variant="body2" color="text">
                      Plano vitalício concedido. Não há cobrança nem assinatura para gerenciar.
                    </MDTypography>
                  </MDBox>
                ) : isPremium ? (
                  <MDBox>
                    <MDTypography variant="body2" color="text" mb={2}>
                      Gerencie seus métodos de pagamento, visualize faturas e altere ou cancele seu plano pelo portal seguro.
                    </MDTypography>
                    <MDButton variant="gradient" color={sidenavColor} fullWidth onClick={openPortal} disabled={busy}>
                      {pendingAction === "portal" ? "Redirecionando..." : "Gerenciar assinatura"}
                    </MDButton>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      Seja Premium
                    </MDTypography>
                    <MDBox component="ul" sx={{ listStyle: "none", p: 0, mb: 2 }}>
                      {PREMIUM_BENEFITS.map((benefit) => (
                        <MDBox component="li" key={benefit} display="flex" alignItems="center" mb={0.5}>
                          <Icon color="success" fontSize="small" sx={{ mr: 1 }}>check_circle</Icon>
                          <MDTypography variant="button" color="text">{benefit}</MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <MDButton
                          variant="gradient"
                          color={sidenavColor}
                          fullWidth
                          disabled={busy || !user}
                          onClick={() => startCheckout("monthly")}
                        >
                          {pendingAction === "monthly"
                            ? "Redirecionando..."
                            : `${PLANS.monthly.label} ${PLANS.monthly.price}${PLANS.monthly.period}`}
                        </MDButton>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDButton
                          variant="outlined"
                          color={sidenavColor}
                          fullWidth
                          disabled={busy || !user}
                          onClick={() => startCheckout("yearly")}
                        >
                          {pendingAction === "yearly" ? "Redirecionando..." : `${PLANS.yearly.label} (${PLANS.yearly.price})`}
                        </MDButton>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Billing;
